import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Branding({ restaurant, onUpdate }) {
  const [f, setF] = useState({ name: "", description: "", primary_color: "#1A1A1A", accent_color: "#D4AF37", background_color: "#FFFFFF", logo_url: "", currency: "EUR" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (restaurant) setF({
      name: restaurant.name || "",
      description: restaurant.description || "",
      primary_color: restaurant.primary_color || "#1A1A1A",
      accent_color: restaurant.accent_color || "#D4AF37",
      background_color: restaurant.background_color || "#FFFFFF",
      logo_url: restaurant.logo_url || "",
      currency: restaurant.currency || "EUR",
    });
  }, [restaurant]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, logo_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Logo caricato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const { data } = await api.put("/restaurants/mine", f);
      onUpdate?.(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="branding-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Brand</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Personalizza</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="p-6 bg-card border border-border rounded-2xl space-y-4">
          <div>
            <Label>Nome ristorante</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="brand-name-input" />
          </div>
          <div>
            <Label>Descrizione</Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="brand-desc-input" />
          </div>
          <div>
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              {f.logo_url && <img src={f.logo_url} alt="" className="h-16 w-16 rounded-lg object-cover" />}
              <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="brand-logo-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brand-upload-btn">
                <Upload className="h-4 w-4 mr-2" /> {uploading ? "…" : "Carica logo"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["primary_color", "Colore primario"],
              ["accent_color", "Colore accento"],
              ["background_color", "Sfondo"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-10 w-10 rounded border border-border" data-testid={`brand-${k}-input`} />
                  <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label>Valuta</Label>
            <Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} data-testid="brand-currency-input" />
          </div>
          <Button onClick={save} className="pill-btn bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))]" data-testid="brand-save-btn">Salva</Button>
        </div>

        <div className="p-6 rounded-2xl border border-border tetris-shadow flex flex-col items-center gap-4"
             style={{ background: f.background_color, color: f.primary_color }}>
          <div className="text-xs uppercase tracking-widest opacity-60">Anteprima</div>
          {f.logo_url && <img src={f.logo_url} alt="" className="h-16 w-16 rounded-full object-cover" />}
          <div className="font-display text-3xl font-black tracking-tighter text-center">{f.name || "Il tuo ristorante"}</div>
          <div className="text-sm opacity-70 text-center">{f.description || "La tua descrizione qui."}</div>
          <button className="pill-btn px-6 py-2 mt-2" style={{ background: f.accent_color, color: "#fff" }}>Ordina</button>
        </div>
      </div>
    </div>
  );
}
